import { createTheme, responsiveFontSizes } from '@material-ui/core/styles';
import { orange, text, createEditorPalette } from './colors';

let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    orange,
    primary: {
      lighter: orange.light,
      light: orange.medium,
      main: orange.dark,
      dark: orange.active,
      contrastText: '#fff',
    },
    secondary: {
      light: text.grey1,
      main: '#ffffff',
      dark: text.grey3,
    },
    text: {
      primary: text.black,
      secondary: text.grey1,
      disabled: text.grey2,
    },
    sketch: {
      ...text,
    },
  },
  shape: {
    borderRadius: 3,
    largeButtonSize: 127,
    megaLargeButtonSize: 205,
  },
  typography: {
    fontFamily: ['Source Sans Pro', 'sans-serif'],
    h1: {
      fontSize: 56,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    h2: {
      fontSize: 48,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    h3: {
      fontSize: 36,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    h4: {
      fontSize: 32,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    h5: {
      fontSize: 28,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    h6: {
      fontSize: 21,
      lineHeight: 1.5,
      fontWeight: 400,
    },

    subtitle1: {
      fontSize: 14,
      lineHeight: 1.3,
      fontWeight: 400,
    },

    subtitle2: {
      fontSize: 24,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    body1: {
      fontSize: 18,
      lineHeight: 1.3,
      fontWeight: 400,
    },

    body2: {
      fontSize: 16,
      // lineHeight: 1.6,
      fontWeight: 400,
    },

    caption: {
      fontSize: 12,
      // lineHeight: 1.44,
      fontWeight: 400,
    },

    overline: {
      fontSize: 22,
      // lineHeight: 1.6,
      fontWeight: 400,
      textTransform: 'none',
      lineHeight: 1,
    },
    button: {
      fontSize: 16,
      // lineHeight: 1.6,
      fontWeight: 400,
      textTransform: 'none',
    },
  },
  props: {
    MuiButton: {
      variant: 'contained',
      color: 'primary',
      // disableRipple: true,
    },
    MuiSvgIcon: {
      // color: 'primary',
      // fontSize: 'small',
    },
  },
  mixins: {
    maxWidth: () => ({
      maxWidth: 1024,
      margin: 'auto',
    }),
  },
});

// theme = responsiveFontSizes(theme)

theme.editorPalette = createEditorPalette(theme.palette, {
  secondary: {
    light: theme.palette.grey[200],
    main:  theme.palette.grey[500],
    dark:  theme.palette.grey[800],
  },
});

theme.overrides = {
  MuiTypography: {
    h1: {
      [theme.breakpoints.only('xs')]: {
        fontSize: 42,
      },
    },
    h2: {
      [theme.breakpoints.only('xs')]: {
        fontSize: theme.typography.h3.fontSize,
      },
    },
    h5: {
      [theme.breakpoints.only('xs')]: {
        fontSize: theme.typography.subtitle2.fontSize,
      },
    },
    root: {
      '&[data-shadow]': {
        textShadow: `0 0 5px ${theme.palette.grey[900]}`,
      },
      '&[data-underline-white]': {
        '&:after': {
          backgroundColor: theme.palette.common.white,
        },
      },
      '&[data-underline]': {
        textAlign: 'center',
        marginBottom: theme.spacing(8),
        position: 'relative',
        '&:after': {
          content: "''",
          width: 128,
          height: 2,
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: `translate(-50%, ${theme.spacing(2)}px)`,
          backgroundColor: theme.palette.primary.main,
        },
      },
      '&[data-underline-less-spacing]': {
        marginBottom: theme.spacing(4),
      },
    },
  },
  MuiAppBar: {
    root: {
      boxShadow: 'none',
    },
  },
  MuiSvgIcon: {
    root: {
      fontSize: 28,
    },
    fontSizeLarge: {
      fontSize: 48,
    },
    colorDisabled: {
      color: theme.palette.text.disabled,
    },
  },
  MuiButton: {
    root: {
      lineHeight: 1.65,
      // minWidth: 148,
    },
    textSecondary: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      '&:hover': {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        '@media (hover: none)': {
          backgroundColor: theme.palette.common.white,
        },
      },
    },
    outlinedSecondary: {
      backgroundColor: 'transparent',
      color: theme.palette.common.white,
    },
    outlinedPrimary: {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '@media (hover: none)': {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    outlined: {
      backgroundColor: theme.palette.common.white,
    },
    textSecondary: {
      color: text.grey1,
    },
    contained: {
      backgroundColor: theme.palette.common.white,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
      },
      '&:active': {
        boxShadow: 'none',
      },
    },
    containedSecondary: {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
      },
    },
    containedSizeLarge: {
      fontWeight: 'bold',
      color: theme.palette.common.main,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    sizeLarge: {
      // height: theme.shape.largeButtonSize,
      fontSize: 20,
    },
    textSizeSmall: {
      fontSize: 14,
      padding: theme.spacing(0, 1),
    },
    startIcon: {
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0.5),
    },
    iconSizeMedium: {
      '& > *:first-child': {
        fontSize: 16,
      },
    },
  },
  MuiListItem: {
    gutters: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  MuiDivider: {
    root: {
      backgroundColor: orange.dark,
    },
    light: {
      backgroundColor: theme.palette.grey[300],
    },
  },
  MuiFilledInput: {
    root: {
      backgroundColor: theme.palette.sketch.grey3,
    },
  },
  MuiCheckbox: {
    root: {
      padding: theme.spacing(1),
    }
  }
};

export default theme;
